import type {
  Issue,
  IssueKind,
  MemberLeague,
  MemberRequirements,
} from '../shared/contracts';
import type { PortalSection } from '../shared/pages';
import { countLabel } from './countLabel';

export interface IssueDetails {
  badgeLabel: string;
  label: (count: number) => string;
}

export const ISSUES = {
  'activity-waiver': {
    badgeLabel: 'Waiver',
    label: (count) => `${countLabel(count, 'waiver')} to sign`,
  },
  'missing-waiver': {
    badgeLabel: 'Missing waivers',
    label: (count) => `${countLabel(count, 'player')} missing a waiver`,
  },
  'missing-logo': {
    badgeLabel: 'Missing team logo',
    label: (count) => `${countLabel(count, 'team')} without a logo`,
  },
  'profile-incomplete': {
    badgeLabel: 'Profile incomplete',
    label: (count) =>
      `${countLabel(count, 'item')} to complete on your profile`,
  },
} satisfies Record<IssueKind, IssueDetails>;

export type SectionIssues = Partial<Record<PortalSection, readonly Issue[]>>;

export function mergeIssues(lists: readonly (readonly Issue[])[]): Issue[] {
  const counts = new Map<IssueKind, number>();
  for (const issue of lists.flat()) {
    counts.set(issue.kind, (counts.get(issue.kind) ?? 0) + issue.count);
  }
  return [...counts.entries()].map(([kind, count]) => ({ kind, count }));
}

export function issueLabel(issue: Issue): string {
  return ISSUES[issue.kind].label(issue.count);
}

export function issueSummary(issues: readonly Issue[]): string {
  return issues.map(issueLabel).join(', ');
}

export function sectionIssues({
  requirements,
  league,
}: {
  requirements: MemberRequirements | undefined;
  league: MemberLeague | undefined;
}): SectionIssues {
  const sections: SectionIssues = {};
  const outstanding = requirements?.outstandingRequirements ?? [];
  if (outstanding.length > 0) {
    sections.profile = [
      { kind: 'profile-incomplete', count: outstanding.length },
    ];
  }
  const activityWaivers =
    requirements?.waiversToSign.filter(
      (waiver) => !waiver.required && waiver.activities?.length,
    ).length ?? 0;
  if (activityWaivers > 0) {
    sections.profile = [
      ...(sections.profile ?? []),
      { kind: 'activity-waiver', count: activityWaivers },
    ];
  }
  const leagueIssues = mergeIssues(
    (league?.league ?? []).map((entry) => entry.issues),
  );
  if (leagueIssues.length > 0) sections.league = leagueIssues;
  return sections;
}
